<template>
  <div class="user-container">
    <el-card shadow="never">
      <!-- 工具栏 -->
      <div class="toolbar">
        <el-input
          v-model.trim="keyword"
          placeholder="搜索应用名"
          clearable
          prefix-icon="el-icon-search"
          style="width: 240px"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch"
        />
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
        <el-button type="success" icon="el-icon-plus" @click="handleAdd">新增应用</el-button>
      </div>

      <!-- 表格 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        border
        stripe
        style="width: 100%"
      >
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column prop="app_id" label="应用id" width="180" />
        <el-table-column prop="app_name" label="应用名称" width="180" />
        <el-table-column prop="app_project" label="所属项目" width="90" />
        <el-table-column prop="app_region" label="所属地域" width="90" />
        <el-table-column prop="callbackurl" label="回调地址" min-width="480" />
        <el-table-column label="应用密钥" width="120" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" icon="el-icon-key" @click="handleViewSecret(row)">
              查看 / 重置
            </el-button>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="160">
          <template slot-scope="{ row }">
            {{ row.createdAt ? row.createdAt.replace('T', ' ').slice(0, 19) : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" icon="el-icon-edit" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button
              type="text"
              size="small"
              class="delete-btn"
              icon="el-icon-delete"
              @click="handleDelete(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          :current-page.sync="page"
          :page-size="size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          background
          @size-change="handleSizeChange"
          @current-change="fetchData"
        />
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="460px"
      :close-on-click-modal="false"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="80px">
        <el-form-item label="应用id" prop="app_id">
          <el-input v-model.trim="form.app_id" placeholder="请输入应用id" />
        </el-form-item>
        <el-form-item label="应用名称" prop="app_name">
          <el-input v-model.trim="form.app_name" placeholder="请输入应用名称" />
        </el-form-item>
        <el-form-item label="所属项目" prop="app_project">
          <el-input v-model.trim="form.app_project" placeholder="请输入应用项目" />
        </el-form-item>
        <el-form-item label="所属地域" prop="app_region">
          <el-input v-model.trim="form.app_region" placeholder="请输入应用地域" />
        </el-form-item>
        <el-form-item label="回调地址" prop="callbackurl">
          <el-input v-model.trim="form.callbackurl" placeholder="请输入应用地址" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">
          确 定
        </el-button>
      </div>
    </el-dialog>

    <!-- 应用密钥弹窗 -->
    <el-dialog
      title="应用密钥"
      :visible.sync="secretVisible"
      width="560px"
      :close-on-click-modal="false"
    >
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        title="密钥只由 SSO 与业务系统后端持有"
        description="业务系统调 /api/sso/sso_verify 时用它对请求签名。请勿下发到前端或泄露给第三方；重置后旧密钥立即失效，需同步更新业务系统配置。"
        style="margin-bottom: 16px"
      />

      <el-form label-width="90px">
        <el-form-item label="应用ID">
          <span>{{ secretInfo.app_id }}</span>
        </el-form-item>
        <el-form-item label="应用名称">
          <span>{{ secretInfo.app_name }}</span>
        </el-form-item>
        <el-form-item label="应用密钥">
          <div v-loading="secretLoading" class="secret-box">
            <code v-if="secretInfo.app_secret">{{ secretInfo.app_secret }}</code>
            <span v-else class="secret-empty">未生成</span>
          </div>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="secretVisible = false">关 闭</el-button>
        <el-button
          v-if="secretInfo.app_secret"
          icon="el-icon-document-copy"
          @click="copySecret"
        >
          复制
        </el-button>
        <el-button
          type="danger"
          icon="el-icon-refresh"
          :loading="secretResetting"
          @click="handleResetSecret"
        >
          重置密钥
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getAppList,
  createApp,
  updateApp,
  deleteApp,
  getAppSecret,
  resetAppSecret
} from '../api/app';

export default {
  name: 'App',
  data() {
    return {
      loading: false,
      tableData: [],
      keyword: '',
      page: 1,
      size: 10,
      total: 0,
      // 弹窗
      dialogVisible: false,
      submitting: false,
      isEdit: false,
      form: {
        app_id: '',
        app_name: '',
        app_project: '',
        app_region: '',
        callbackurl: ''
      },
      // 密钥弹窗
      secretVisible: false,
      secretLoading: false,
      secretResetting: false,
      secretInfo: { app_id: '', app_name: '', app_secret: '' },
      rules: {
        app_id: [{ required: true, message: '请输入应用id', trigger: 'blur' }],
        app_name: [{ required: true, message: '请输入应用名称', trigger: 'blur' }],
        app_project: [{ required: true, message: '请输入应用项目', trigger: 'blur' }],
        app_region: [{ required: true, message: '请输入应用地域', trigger: 'blur' }],
        callbackurl: [{ required: true, message: '请输入回调地址', trigger: 'blur' }],
      }
    };
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑应用' : '新增应用';
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await getAppList({
          page: this.page,
          size: this.size,
          keyword: this.keyword
        });
        this.tableData = data.data.list;
        this.total = data.data.total;
      } catch (err) {
        this.$message.error(err.message);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.page = 1;
      this.fetchData();
    },
    handleSizeChange(size) {
      this.size = size;
      this.page = 1;
      this.fetchData();
    },
    handleAdd() {
      this.isEdit = false;
      this.form = { app_id: '', app_name: '', app_project: '', app_region: '', callbackurl: '' };
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.form.clearValidate());
    },
    handleEdit(row) {
      this.isEdit = true;
      this.form = Object.assign({}, row)
      this.form.id = row._id
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.form.clearValidate());
    },
    async handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        this.submitting = true;
        try {
          if (this.isEdit) {
            await updateApp(this.form);
          } else {
            await createApp(this.form);
          }
          this.$message.success(this.isEdit ? '更新成功' : '创建成功');
          this.dialogVisible = false;
          this.fetchData();
        } catch (err) {
          this.$message.error(err.message);
        } finally {
          this.submitting = false;
        }
      });
    },
    // 查看应用密钥
    async handleViewSecret(row) {
      console.log(row)
      this.secretVisible = true;
      this.secretLoading = true;
      this.secretInfo = {
        _id: row._id,
        app_id: row.app_id,
        app_name: row.app_name,
        app_secret: ''
      };
      try {
        const { data } = await getAppSecret(row._id);
        this.secretInfo.app_secret = (data.data && data.data.app_secret) || '';
      } catch (err) {
        this.$message.error(err.message || '密钥获取失败');
      } finally {
        this.secretLoading = false;
      }
    },
    // 重置密钥
    handleResetSecret() {
      this.$confirm(
        '重置后旧密钥立即失效，业务系统需同步更新配置才能继续验签。确定重置吗？',
        '提示',
        { confirmButtonText: '确定重置', cancelButtonText: '取消', type: 'warning' }
      ).then(async () => {
        this.secretResetting = true;
        try {
          const { data } = await resetAppSecret(this.secretInfo._id);
          this.secretInfo.app_secret = (data.data && data.data.app_secret) || '';
          this.$message.success('密钥已重置');
        } catch (err) {
          this.$message.error(err.message || '重置失败');
        } finally {
          this.secretResetting = false;
        }
      }).catch(() => {});
    },
    // 复制密钥
    copySecret() {
      const text = this.secretInfo.app_secret;
      if (!text) return;
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success('已复制'))
          .catch(() => this.$message.warning('复制失败，请手动选中'));
        return;
      }
      // 非安全上下文降级：选中文本由用户手动复制
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
        this.$message.success('已复制');
      } catch (e) {
        this.$message.warning('复制失败，请手动选中');
      }
      document.body.removeChild(el);
    },
    handleDelete(row) {
      this.$confirm(`确定删除应用 "${row.app_name}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteApp(row._id);
          this.$message.success('删除成功');
          // 删除后如果当前页空了，退回上一页
          if (this.tableData.length === 1 && this.page > 1) {
            this.page--;
          }
          this.fetchData();
        } catch (err) {
          this.$message.error(err.message);
        }
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.user-container {
  padding: 10px;
}
.toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}
.delete-btn {
  color: #F56C6C;
}
.pagination {
  margin-top: 20px;
  text-align: right;
}
.secret-box {
  padding: 8px 10px;
  background: #f5f7fa;
  border-radius: 4px;
  word-break: break-all;
  font-family: Consolas, Monaco, monospace;
  font-size: 13px;
  min-height: 36px;
}
.secret-empty {
  color: #c0c4cc;
}
</style>
